import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Info, Menu, Settings } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Popover } from '@/components/ui/popover';
import { BranchSelector } from '@/components/layout/BranchSelector';
import { isBranchScopedPath } from '@/components/layout/isBranchScopedPath';
import { useT } from '@/contexts/I18nContext';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useBranchDocumentTitle } from '@/hooks/useBranchDocumentTitle';
import { cn } from '@/lib/utils';

interface HeaderProps {
  onOpenSidebar: () => void;
}

export function Header({ onOpenSidebar }: HeaderProps) {
  const { t } = useT();
  const { title, description, center, centerMobile, actions, extra, settingsTo } = usePageHeader();
  const location = useLocation();
  // Keep the browser tab title in sync with the selected branch
  // ("Linza Metrics — <branch>"). Lives here (authed shell) so it never
  // fetches zones on /login and shares the BranchSelector's SWR cache.
  useBranchDocumentTitle();
  // Hide the branch selector on screens that aren't scoped to a single
  // branch: portfolio (multi-branch overview), admin pages (system-wide
  // device + user management), and account settings (user-scoped).
  const showBranchSelector = isBranchScopedPath(location.pathname);

  // DESKTOP: branch selector left, page controls centered, title block (or the
  // page's actions) right — unchanged.
  //
  // MOBILE (Алексей 17.09, AI12.6): the bar is thin and names the SECTION —
  // hamburger, title, then a small inline (i) or, when the section has
  // settings, a small gear. The branch picker moved into the drawer (a large
  // block at its top, like switching a workspace in Notion/Figma); page
  // controls go to the page's own second-level bar (MobileSubHeader).
  return (
    <header className="sticky top-0 z-30 flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background/85 px-2 backdrop-blur md:h-14 md:gap-3 md:px-4">
      {/* THREE ZONES on desktop. Left + right are flex-1 (so the center sits
          geometrically centered) and min-w-0 (so they TRUNCATE under pressure);
          the center is shrink-0 (natural width, never shrinks → its nowrap
          controls can't overflow into the neighbours). */}
      <div className="flex min-w-0 flex-1 items-center gap-1 md:gap-3">
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0 md:hidden"
          onClick={onOpenSidebar}
          aria-label={t('common.openMenu')}
        >
          <Menu className="size-4" />
        </Button>

        {showBranchSelector && (
          <BranchSelector className="w-full min-w-0 max-w-[280px] max-md:hidden" />
        )}

        {title && (
          <span className="min-w-0 truncate text-[15px] font-semibold text-foreground md:hidden">
            {title}
          </span>
        )}
        <HeaderInfo title={title} description={description} extra={extra} settingsTo={settingsTo} />
        {/* Page controls a page still wants in the mobile bar itself. */}
        {centerMobile && <div className="ml-auto flex shrink-0 items-center md:hidden">{centerMobile}</div>}
      </div>

      {center && <div className="hidden shrink-0 items-center md:flex">{center}</div>}

      <div className="hidden min-w-0 flex-1 items-center justify-end gap-2 md:flex">
        {actions ? (
          <div className="flex items-center gap-2">{actions}</div>
        ) : (
          title && (
            // Подзаголовок налезал на кнопки центра («Все данные», «Полный кадр») на средних
            // экранах (Алексей 23.10 #2133): в flex-col items-end span шире колонки и truncate не
            // срабатывал. Теперь блок обрезается по своей ширине, описание — только на широких.
            <div className="flex min-w-0 max-w-full flex-col items-end overflow-hidden text-right">
              <span className="max-w-full truncate text-sm font-semibold text-foreground">{title}</span>
              {description && (
                <span className="hidden max-w-full truncate text-xs text-muted-foreground xl:block">{description}</span>
              )}
            </div>
          )
        )}
      </div>
    </header>
  );
}

// Mobile-only small inline icon next to the title → a popover with the page's
// description, any extra content the page injected, and a settings link when
// the section has settings (then the icon is a gear instead of (i)).
function HeaderInfo({
  title,
  description,
  extra,
  settingsTo,
}: {
  title: string | null;
  description: string | null;
  extra: React.ReactNode | null;
  settingsTo: string | null;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  // Ссылки внутри (настройки, «добавить вкладки») уводят со страницы — закрываемся.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  if (!description && !extra && !settingsTo) return null;
  const zone = new URLSearchParams(location.search).get('zone');
  const Icon = settingsTo ? Settings : Info;
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
        aria-label={settingsTo ? t('common.settings') : (title ?? t('common.info'))}
      >
        <Icon className="size-4" />
      </button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={ref} side="bottom" align="start">
        <div className="w-64 max-w-[80vw] space-y-2 rounded-xl border border-border bg-card p-3 shadow-lg">
          {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
          {description && <div className="text-xs text-muted-foreground">{description}</div>}
          {extra && <div>{extra}</div>}
          {settingsTo && (
            <Link
              to={{ pathname: settingsTo, search: zone ? `?zone=${zone}` : '' }}
              onClick={() => setOpen(false)}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-full')}
            >
              <Settings className="size-3.5" /> {t('common.settings')}
            </Link>
          )}
        </div>
      </Popover>
    </>
  );
}
